import { useCallback, useEffect } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectAvailableFreeGifts } from "@jti/store/selectors/selectAvailableFreeGifts";
import { selectCouponCodes } from "@jti/store/selectors/selectCouponCodes";
import {
  applyCoupons,
  fetchCartItems,
  fetchCartPrices,
  fetchCoupons,
  removeCoupons,
} from "@jti/store/slices/cartSlice";

import { CheckOutSteps } from "@checkout/constants/checkout-constants";
import formatCode from "@checkout/utils/formatCode";

import { PromoCodeView } from "./components/PromoCodeView";

type PromoCodeProps = {
  step?: CheckOutSteps;
};

export const PromoCode = ({ step }: PromoCodeProps) => {
  const dispatch = useDispatch();
  const config = useConfig();

  const cartId = useSelector((state) => state.cart.id);
  const couponCodes = useSelector(selectCouponCodes);
  const coupons = useSelector((state) => state.cart.coupons);
  const errors = useSelector((state) => state.cart.couponErrors);
  const availableFreeGifts = useSelector(selectAvailableFreeGifts) ?? [];

  const { hidePromoCodeBlockOnCheckoutShippingStep, hidePromoCodeBlockOnCheckoutDeliveryStep } = config;

  const shouldHide =
    (step === CheckOutSteps.Shipping && hidePromoCodeBlockOnCheckoutShippingStep) ||
    (step === CheckOutSteps.Delivery && hidePromoCodeBlockOnCheckoutDeliveryStep) ||
    step === CheckOutSteps.Payment;

  useEffect(() => {
    if (cartId) {
      dispatch(fetchCoupons(cartId));
    }
  }, [cartId, dispatch]);

  const handleAddCoupon = useCallback(
    async (couponCode: string) => {
      if (cartId) {
        await dispatch(applyCoupons({ cartId, couponCode: [...couponCodes, formatCode(couponCode)] }));
        dispatch(fetchCoupons(cartId));
        dispatch(fetchCartPrices(cartId));
        dispatch(fetchCartItems({ cartId, silent: true }));
      }
    },
    [cartId, couponCodes, dispatch]
  );

  const handleRemoveAllCoupons = useCallback(async () => {
    if (cartId) {
      const couponCodes = coupons.map(({ code }) => code);
      await dispatch(removeCoupons({ cartId, couponCodes }));
      dispatch(fetchCoupons(cartId));
      dispatch(fetchCartPrices(cartId));
      const hasPromoGiftsInCart = availableFreeGifts.length > 0;
      dispatch(fetchCartItems(hasPromoGiftsInCart ? cartId : { cartId, silent: true }));
    }
  }, [availableFreeGifts.length, cartId, coupons, dispatch]);

  const handleRemoveCoupon = useCallback(
    async (couponCode: string) => {
      if (cartId) {
        await dispatch(removeCoupons({ cartId, couponCodes: [couponCode] }));
        dispatch(fetchCoupons(cartId));
        dispatch(fetchCartPrices(cartId));
        const hasPromoGiftsInCart = availableFreeGifts.length > 0;
        dispatch(fetchCartItems(hasPromoGiftsInCart ? cartId : { cartId, silent: true }));
      }
    },
    [availableFreeGifts.length, cartId, dispatch]
  );

  if (shouldHide) {
    return null;
  }

  return (
    <PromoCodeView
      errors={errors}
      items={coupons}
      onAddCoupon={handleAddCoupon}
      onRemoveAllCoupons={handleRemoveAllCoupons}
      onRemoveCoupon={handleRemoveCoupon}
    />
  );
};
